// deps
import React, { memo, ReactElement, useMemo, useCallback } from "react";
import classnames from "classnames";

// components
import PaymentGroupContainer from "../../components/Container";
import PaymentGroupTitle from "../../components/Title";
import { ArrowSvgIcon } from "@payment-ui/widgets/arrow-svg-icon";

// constants
import { TEXTS, OTHER_PAYMENTS_ICON } from "@payment-ui/constants/index";

// utils
import { format, getCloudinaryUrl, useEffectOnce } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";

// styles
import styles from "./OtherPayments.scss";

// test-ids
import { PAYMENT_GROUP_TEST_IDS } from "@swiggy-private/payment-test-ids";

interface OtherPaymentsProps {
    couponCode?: string;
    rewardName?: string;
    onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
    hideTitle?: boolean;
    heading?: string;
    iconUrl?: string;
    containerClassName?: string;
    onLoad?: () => void;
}

function OtherPaymentsIcon(props: { iconUrl?: string }): ReactElement {
    const { iconUrl = OTHER_PAYMENTS_ICON } = props;

    return <img crossOrigin="anonymous" src={getCloudinaryUrl(iconUrl)} width={32} height={32} />;
}

function OtherPayments(props: OtherPaymentsProps): ReactElement {
    const {
        hideTitle,
        heading,
        couponCode,
        rewardName,
        iconUrl,
        containerClassName,
        onLoad,
    } = props;

    const contentInfoText = useMemo(() => {
        if (!isEmpty(rewardName)) {
            return format(TEXTS.X_REWARD_WONT_BE_APPLICABLE_ON_OTHER_METHODS, rewardName);
        } else if (!isEmpty(couponCode)) {
            return format(TEXTS.COUPON_X_NOT_APPLICABLE_ON_OTHER_METHODS, couponCode);
        }
        return undefined;
    }, [couponCode, rewardName]);

    const containerClass = classnames(containerClassName, styles.content);

    useEffectOnce(() => {
        onLoad?.();
    });

    const handleOnClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            props.onClick(e);
        },
        [props],
    );

    return (
        <PaymentGroupContainer>
            {!hideTitle && (
                <PaymentGroupTitle
                    title={TEXTS.OTHER_PAYMENT_METHODS}
                    data-testid={PAYMENT_GROUP_TEST_IDS.TITLE.OTHER_PAYMENTS}
                />
            )}
            <div className={containerClass} onClick={handleOnClick}>
                <div className={styles.contentIconContainer}>
                    <OtherPaymentsIcon iconUrl={iconUrl} />
                </div>
                <div className={styles.contentContainer}>
                    <div className={styles.contentTitle} data-testid={PAYMENT_GROUP_TEST_IDS.OTHER_METHODS.CONTENT}>
                        {heading ?? TEXTS.PAY_USING_OTHER}
                    </div>
                    {!isEmpty(contentInfoText) && (
                        <div
                            className={styles.contentInfo}
                            data-testid={PAYMENT_GROUP_TEST_IDS.OTHER_METHODS.CONTENT_MESSAGE}
                        >
                            {contentInfoText}
                        </div>
                    )}
                </div>
                <ArrowSvgIcon className={styles.contentArrowIcon} />
            </div>
        </PaymentGroupContainer>
    );
}

export default memo(OtherPayments);
